import { createStore, PartialDeep } from '@servicetitan/financing-utils-shared';
import { useState, useRef } from 'react';
import { LoanApplicationSubmitSource2 } from '@servicetitan/financing-api-client';
import { PrimaryApplicantData } from '../applicant/schema';
import { MailingAddressData } from '../mailing-address/schema';
import { ServicePropertyData } from '../service-property/schema';
import { CoApplicantData } from '../co-applicant/schema';
import { ExternalApplicationData } from '../external-application/schema';

export interface ApplicationData {
    applicant: PartialDeep<PrimaryApplicantData>;
    mailingAddress: PartialDeep<MailingAddressData>;
    serviceProperty: PartialDeep<ServicePropertyData>;
    coApplicant: PartialDeep<CoApplicantData>;
    externalApplication: PartialDeep<ExternalApplicationData>;
    source: LoanApplicationSubmitSource2;
}

export const hasCoApplicant = (
    data: ApplicationData,
): data is Exclude<ApplicationData, { coApplicant: { hasCoApplicant: false } }> =>
    data.coApplicant.hasCoApplicant ?? false;

function useApplicationData(
    initialState: ApplicationData = {
        applicant: {},
        mailingAddress: {
            workPerformedAtAddress: true,
        },
        serviceProperty: { primaryApplicant: {} },
        coApplicant: { coApplicant: {} },
        externalApplication: {},
        source: LoanApplicationSubmitSource2.Unknown,
    },
) {
    const [applicationData, setApplicationData] = useState(initialState);
    const [originalState] = useState(JSON.parse(JSON.stringify(initialState)));
    const [isEditing, setIsEditing] = useState(false);
    const editModeSnapshot = useRef<ApplicationData | null>(null);

    const captureEditModeState = () => {
        editModeSnapshot.current = JSON.parse(JSON.stringify(applicationData));
        setIsEditing(true);
    };

    const resetToEditModeState = () => {
        if (editModeSnapshot.current) {
            setApplicationData(JSON.parse(JSON.stringify(editModeSnapshot.current)));
        }
        setIsEditing(false);
    };

    const resetToInitialState = () => {
        setApplicationData(JSON.parse(JSON.stringify(originalState)));
        setIsEditing(false);
    };

    const setEditMode = (editing: boolean) => {
        setIsEditing(editing);
    };

    return {
        applicationData,
        setApplicationData,
        resetToInitialState,
        captureEditModeState,
        resetToEditModeState,
        isEditing,
        setEditMode,
    };
}

export const ApplicationDataStore = createStore(useApplicationData);

export type ApplicationDataStore = ReturnType<typeof useApplicationData>;
